import React, { ChangeEventHandler, PureComponent } from 'react';

import { TravelInformations } from '@bluevalet/common-ui';
import { WithTranslation, withTranslation } from 'react-i18next';
import i18next from 'i18next';
import { dateAndHourToMoment } from '../../../utils/dates';
import API from '../../common/api';
import { BVScheduledTransport, Site } from '../../common/model';

export enum TripPrefix {
  DEPARTURE_PREFIX = 'departure',
  ARRIVAL_PREFIX = 'arrival',
}

interface Props {
  prefix: TripPrefix;
  site: string;
  siteType: Site.Kind;
  date: string;
  tripNumber: string | null;
  tripDetails: BVScheduledTransport | null;
  onTripNumberChange: (value?: string) => void;
  onTripDetailsChange: (value?: object | null) => void;
  readOnly: boolean;
  hour?: string;
}

interface State {
  fetchAbortController: AbortController;
  trip: { isFetchingInformations?: boolean };
}

class TripNumber extends PureComponent<Props & WithTranslation, State> {
  constructor(props: Props & WithTranslation) {
    super(props);
    this.state = { fetchAbortController: new AbortController(), trip: { isFetchingInformations: false } };
  }

  componentDidMount() {
    const { prefix, site, date, tripNumber, tripDetails } = this.props;
    if (!tripDetails) this.fetchTripInfos(prefix, site, date, tripNumber);
  }

  componentDidUpdate(prevProps: Props) {
    const { prefix, site, date, tripNumber } = this.props;

    if (date !== prevProps.date) this.fetchTripInfos(prefix, site, date, tripNumber);
  }

  onTravelInformationsChange: ChangeEventHandler<HTMLInputElement> = event => {
    const { value } = event.target;
    const { prefix, site, date, onTripNumberChange } = this.props;

    this.state.fetchAbortController.abort();

    this.setState({ fetchAbortController: new AbortController() }, () => {
      onTripNumberChange(value);
      this.fetchTripInfos(prefix, site, date, value, this.state.fetchAbortController.signal);
    });
  };

  onClearTripNumber = () => {
    const { onTripNumberChange, onTripDetailsChange } = this.props;
    onTripNumberChange('');
    onTripDetailsChange();
  };

  getStatus = () => {
    const { tripNumber } = this.props;
    const {
      trip: { isFetchingInformations },
    } = this.state;
    if (isFetchingInformations) return 'loading';

    if (tripNumber !== '') return 'loaded';

    return '';
  };

  getInputProps = () => {
    const { site, date, hour, readOnly } = this.props;

    return {
      readOnly,
      disabled: !site || !date || dateAndHourToMoment(date, hour).isBefore(),
      success: false, // to prevent green border
      error: false, // to prevent red border
      message: '', // error message
    };
  };

  static getSiteLabel(result: BVScheduledTransport | null, prefix: TripPrefix) {
    if (result) {
      switch (prefix) {
        case TripPrefix.DEPARTURE_PREFIX:
          switch (result.type) {
            case BVScheduledTransport.Type.Flight:
              return result.departureAirport.name;
            case BVScheduledTransport.Type.Train:
              return result.departureStation;
          }
          break;
        case TripPrefix.ARRIVAL_PREFIX:
          switch (result.type) {
            case BVScheduledTransport.Type.Flight:
              return result.arrivalAirport.name;
            case BVScheduledTransport.Type.Train:
              return result.arrivalStation;
          }
      }
    }
    return '';
  }

  static getTravelInformationsLabelProperty(prefix: TripPrefix, siteKind: Site.Kind): string | null {
    let direction: 'start' | 'end';
    let type: 'flight' | 'train' | 'gen';
    switch (prefix) {
      case TripPrefix.DEPARTURE_PREFIX:
        direction = 'start';
        break;
      case TripPrefix.ARRIVAL_PREFIX:
        direction = 'end';
        break;
      default:
        return null;
    }
    switch (siteKind) {
      case Site.Kind.Airport:
        type = 'flight';
        break;
      case Site.Kind.Station:
        type = 'train';
        break;
      default:
        type = 'gen';
    }
    return `booking-trip_${direction}_${type}_number`;
  }

  static getTravelInformationsName(prefix: TripPrefix) {
    if (prefix === TripPrefix.DEPARTURE_PREFIX) return 'startTripNumber';

    if (prefix === TripPrefix.ARRIVAL_PREFIX) return 'endTripNumber';

    return null;
  }

  fetchTripInfos = async (
    prefix: TripPrefix,
    site: string,
    date: string,
    value: string | null,
    signal?: AbortSignal,
  ) => {
    const { t, onTripDetailsChange } = this.props;
    if (value && value.toLowerCase() !== t('booking-trip_unknown_number_sel').toLowerCase()) {
      this.setState({ trip: { isFetchingInformations: true } });
      const results = await API.getTripInfos(prefix, site, date, value, signal).catch(() => null);
      this.setState({ trip: { isFetchingInformations: false } });
      onTripDetailsChange(results);
    } else {
      onTripDetailsChange();
    }
  };

  render() {
    const { t, prefix, tripNumber, tripDetails, siteType } = this.props;

    const name = TripNumber.getTravelInformationsName(prefix);
    const label = TripNumber.getTravelInformationsLabelProperty(prefix, siteType);
    const site = tripDetails ? TripNumber.getSiteLabel(tripDetails, prefix) : null;

    return (
      <div className="trip-informations">
        <TravelInformations
          type={prefix}
          name={name ?? undefined}
          label={label ? `${t(label)} *` : undefined}
          site={site ?? undefined}
          message={t('booking-trip_travel_not_found')}
          value={tripNumber}
          onChange={this.onTravelInformationsChange}
          onClearValue={this.onClearTripNumber}
          status={this.getStatus()}
          results={tripDetails ? [tripDetails] : []}
          inputProps={this.getInputProps()}
          lang={i18next.language}
        />
      </div>
    );
  }
}

export default withTranslation()(TripNumber);
